import React, { useState, useEffect, useCallback } from 'react';
import { FiLock, FiUnlock, FiCheck, FiSend, FiDollarSign, FiSettings } from 'react-icons/fi';
import API from '../../utils/api';
import toast from 'react-hot-toast';

const monthKey = (date = new Date()) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

const addDays = (dateStr, days) => {
  if (!dateStr || !days) return null;
  const d = new Date(dateStr);
  d.setDate(d.getDate() + parseInt(days));
  return d.toISOString().slice(0, 10);
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const lastDayOfCurrentMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
};

const MonthlyBillingCard = ({ businessId, companyName, hasCreditLimit, onBillingSettingsChange }) => {
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);

  // Settings edit state
  const [settingsEdit, setSettingsEdit] = useState(false);
  const [settingsForm, setSettingsForm] = useState({ monthlyBillingEnabled: false, paymentDueDays: '' });
  const [settingsSaving, setSettingsSaving] = useState(false);

  // Mark bill sent
  const [billSentSaving, setBillSentSaving] = useState(false);

  // Mark payment received
  const [paymentModal, setPaymentModal] = useState(false);
  const [paymentMonth, setPaymentMonth] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [paymentSaving, setPaymentSaving] = useState(false);

  // Unlock modal
  const [unlockModal, setUnlockModal] = useState(false);
  const [unlockReason, setUnlockReason] = useState('');
  const [unlockSaving, setUnlockSaving] = useState(false);

  const fetchBilling = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/monthly-billing/${businessId}`);
      if (data.success) {
        setBilling(data.billing);
        const b = data.billing;
        setSettingsForm({
  monthlyBillingEnabled: !!b.monthlyBillingEnabled,
  paymentDueDays: b.paymentDueDays || '',
});
      }
    } catch {
      toast.error('Failed to load billing info');
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => { fetchBilling(); }, [fetchBilling]);

  const saveSettings = async () => {
    setSettingsSaving(true);
    try {
      const { data } = await API.put(`/monthly-billing/${businessId}/settings`, settingsForm);
      if (data.success) {
        setBilling(prev => ({ ...prev, ...data.billing }));
        setSettingsEdit(false);
        toast.success('Billing settings saved!');
        if (onBillingSettingsChange) onBillingSettingsChange(data.billing);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save settings');
    } finally {
      setSettingsSaving(false);
    }
  };

  const markBillSent = async () => {
    if (!window.confirm(`Confirm: mark bill as sent to ${companyName} for ${monthKey()}?`)) return;
    setBillSentSaving(true);
    try {
      const { data } = await API.post(`/monthly-billing/${businessId}/mark-bill-sent`, {
        month: monthKey(),
        sentAt: new Date().toISOString().slice(0, 10),
      });
      if (data.success) {
        await fetchBilling();
        toast.success('Bill marked as sent!');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to mark bill as sent');
    } finally {
      setBillSentSaving(false);
    }
  };

  const submitPayment = async () => {
    if (!paymentDate) { toast.error('Select payment received date'); return; }
    setPaymentSaving(true);
    try {
      const { data } = await API.post(`/monthly-billing/${businessId}/mark-payment-received`, {
        month: paymentMonth,
        receivedAt: paymentDate,
      });
      if (data.success) {
        await fetchBilling();
        setPaymentModal(false);
        setPaymentMonth('');                                        // ← reset month
        setPaymentDate(new Date().toISOString().slice(0, 10));     // ← reset date to today
        toast.success('Payment logged!');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to log payment');
    } finally {
      setPaymentSaving(false);
    }
  };

  const submitUnlock = async () => {
    if (!unlockReason.trim()) { toast.error('Enter a reason for unlocking'); return; }
    setUnlockSaving(true);
    try {
      const { data } = await API.post(`/monthly-billing/${businessId}/unlock`, { reason: unlockReason });
      if (data.success) {
        await fetchBilling();
        setUnlockModal(false);
        setUnlockReason('');
        toast.success('Credit unlocked!');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to unlock');
    } finally {
      setUnlockSaving(false);
    }
  };

  if (loading) return (
    <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '22px 24px' }}>
      <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Loading billing info…</div>
    </div>
  );

  if (!billing) return null;

  const log = Array.isArray(billing.billingLog) ? billing.billingLog : [];
  const currentMonth = monthKey();
  const currentEntry = log.find(e => e.month === currentMonth);
  const billSentThisMonth = !!currentEntry?.billSentAt;
  const paymentReceivedThisMonth = !!currentEntry?.paymentReceivedAt;
  const dueDate = currentEntry?.billSentAt ? addDays(currentEntry.billSentAt, billing.paymentDueDays) : null;
  const isOverdue = dueDate && new Date().toISOString().slice(0, 10) > dueDate && !paymentReceivedThisMonth;

  return (
    <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '22px 24px' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, paddingBottom: 10, borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: 'var(--primary)' }}>🗓</span>
          <span style={{ fontWeight: 700, fontSize: '0.92rem', color: '#111' }}>Monthly Billing</span>
          {billing.isBillingLocked && (
            <span style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', borderRadius: 99, fontSize: '0.7rem', fontWeight: 700, padding: '2px 10px', display: 'flex', alignItems: 'center', gap: 4 }}>
              <FiLock size={11} /> CREDIT LOCKED
            </span>
          )}
          {billing.monthlyBillingEnabled && !billing.isBillingLocked && (
            <span style={{ background: 'rgba(5,150,105,0.1)', color: '#059669', borderRadius: 99, fontSize: '0.7rem', fontWeight: 700, padding: '2px 10px' }}>
              ACTIVE
            </span>
          )}
        </div>
        <button
          onClick={() => setSettingsEdit(s => !s)}
          style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 12px', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem', background: '#fff', fontFamily: 'var(--font-body)', color: 'var(--text-muted)' }}
        >
          <FiSettings size={12} /> Settings
        </button>
      </div>

      {/* ── Lock Banner ── */}
      {billing.isBillingLocked && (
        <div style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, padding: '14px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontWeight: 700, color: '#ef4444', fontSize: '0.88rem', marginBottom: 3 }}>
              🔒 Credit orders are blocked
            </div>
            <div style={{ fontSize: '0.8rem', color: '#666' }}>
              Locked on {fmtDate(billing.billingLockedAt)} due to overdue payment.
              {billing.billingLockReason && <> Last reason: <em>{billing.billingLockReason}</em></>}
            </div>
          </div>
          <button
            onClick={() => setUnlockModal(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px', background: '#059669', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: '0.83rem', whiteSpace: 'nowrap' }}
          >
            <FiUnlock size={14} /> Unlock Credit
          </button>
        </div>
      )}

      {/* ── Settings Panel ── */}
      {settingsEdit && (
        <div style={{ background: 'var(--bg-soft)', border: '1px solid var(--border)', borderRadius: 10, padding: '16px 18px', marginBottom: 16 }}>
          <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Billing Settings</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                type="checkbox"
                id="mbe-toggle"
                checked={settingsForm.monthlyBillingEnabled}
                onChange={e => setSettingsForm(p => ({ ...p, monthlyBillingEnabled: e.target.checked }))}
                disabled={hasCreditLimit}
                style={{ opacity: hasCreditLimit ? 0.4 : 1, cursor: hasCreditLimit ? 'not-allowed' : 'pointer' }}
              />
              <label htmlFor="mbe-toggle" style={{ fontSize: '0.85rem', cursor: 'pointer', fontWeight: 600 }}>
                Enable monthly billing for this business
              </label>
              {hasCreditLimit && (
                <div style={{ fontSize: '0.75rem', color: '#ef4444', marginTop: 4 }}>
                  ⚠️ This business has a credit limit assigned. Remove it first before enabling monthly billing.
                </div>
              )}
            </div>
            <div>
  <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Bill generation day</label>
  <div
    className="form-control"
    style={{ width: '100%', boxSizing: 'border-box', fontSize: '0.85rem', background: 'var(--bg-soft)', color: '#333', cursor: 'default' }}
  >
    {lastDayOfCurrentMonth()} <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>(last day of this month)</span>
  </div>
</div>
            <div>
              <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Payment due (days after bill)</label>
              <input
                type="number" min={1}
                className="form-control"
                value={settingsForm.paymentDueDays}
                onChange={e => setSettingsForm(p => ({ ...p, paymentDueDays: e.target.value }))}
                placeholder="e.g. 15"
                disabled={hasCreditLimit}
                style={{ width: '100%', boxSizing: 'border-box', fontSize: '0.85rem', opacity: hasCreditLimit ? 0.5 : 1 }}
              />
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 4 }}>Credit auto-locks if payment not logged by this date.</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button onClick={saveSettings} disabled={settingsSaving || hasCreditLimit} className="btn btn-primary" style={{ minWidth: 90, opacity: hasCreditLimit ? 0.5 : 1, cursor: hasCreditLimit ? 'not-allowed' : 'pointer' }}>
              {settingsSaving ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => setSettingsEdit(false)} className="btn btn-outline">Cancel</button>
          </div>
        </div>
      )}

      {/* ── Not enabled state ── */}
      {!billing.monthlyBillingEnabled && !settingsEdit && (
        <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontStyle: 'italic', textAlign: 'center', padding: '20px 0' }}>
          Monthly billing not enabled. Click Settings to configure.
        </div>
      )}

      {/* ── Enabled: current month actions ── */}
      {billing.monthlyBillingEnabled && (
        <>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
              Current month — {currentMonth}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>

              {/* Bill sent status */}
              <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Bill sent?</div>
                {billSentThisMonth ? (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#059669', fontWeight: 700, fontSize: '0.88rem' }}>
                      <FiCheck size={15} /> Sent on {fmtDate(currentEntry.billSentAt)}
                    </div>
                    {dueDate && (
                      <div style={{ fontSize: '0.78rem', color: isOverdue ? '#ef4444' : 'var(--text-muted)', marginTop: 4, fontWeight: isOverdue ? 700 : 400 }}>
                        Payment due: {fmtDate(dueDate)} {isOverdue && '⚠️ OVERDUE'}
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={markBillSent}
                    disabled={billSentSaving}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: '0.82rem' }}
                  >
                    <FiSend size={13} /> {billSentSaving ? 'Saving…' : 'Mark Bill Sent'}
                  </button>
                )}
              </div>

              {/* Payment received status */}
              <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Payment received?</div>
                {paymentReceivedThisMonth ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#059669', fontWeight: 700, fontSize: '0.88rem' }}>
                    <FiCheck size={15} /> Received on {fmtDate(currentEntry.paymentReceivedAt)}
                  </div>
                ) : billSentThisMonth ? (
                  <button
                    onClick={() => { setPaymentMonth(currentMonth); setPaymentModal(true); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: '#059669', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: '0.82rem' }}
                  >
                    <FiDollarSign size={13} /> Log Payment
                  </button>
                ) : (
                  <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>Mark bill sent first</div>
                )}
              </div>
            </div>
          </div>

          {/* ── Config summary ── */}
          <div style={{ display: 'flex', gap: 16, fontSize: '0.78rem', color: 'var(--text-muted)', paddingTop: 10, borderTop: '1px solid var(--border)' }}>
            <span>📅 Bill day: <strong style={{ color: '#333' }}>Last day of month ({lastDayOfCurrentMonth()})</strong></span>
            <span>⏱ Due after: <strong style={{ color: '#333' }}>{billing.paymentDueDays ? `${billing.paymentDueDays} days` : '—'}</strong></span>
          </div>
        </>
      )}

      {/* ── Billing Log (history) ── */}
      {log.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Billing history</div>
          <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr style={{ background: 'var(--bg-soft)' }}>
                  {['Month', 'Bill sent', 'Due date', 'Payment received', 'Status'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {log.map((entry, i) => {
                  const entryDue = entry.billSentAt ? addDays(entry.billSentAt, billing.paymentDueDays) : null;
                  const entryOverdue = entryDue && !entry.paymentReceivedAt && new Date().toISOString().slice(0, 10) > entryDue;
                  let statusBadge, statusColor, statusBg;
                  if (entry.paymentReceivedAt) { statusBadge = 'Paid'; statusColor = '#059669'; statusBg = 'rgba(5,150,105,0.1)'; }
                  else if (entryOverdue || entry.autoLockedAt) { statusBadge = 'Overdue'; statusColor = '#ef4444'; statusBg = 'rgba(239,68,68,0.1)'; }
                  else if (entry.billSentAt) { statusBadge = 'Pending'; statusColor = '#d97706'; statusBg = 'rgba(245,158,11,0.1)'; }
                  else { statusBadge = 'Not sent'; statusColor = '#888'; statusBg = 'rgba(0,0,0,0.05)'; }

                  return (
                    <tr key={i} style={{ borderBottom: i < log.length - 1 ? '1px solid var(--border)' : 'none' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 600, color: '#111' }}>{entry.month}</td>
                      <td style={{ padding: '10px 12px', color: entry.billSentAt ? '#333' : '#aaa', fontStyle: entry.billSentAt ? 'normal' : 'italic' }}>{fmtDate(entry.billSentAt)}</td>
                      <td style={{ padding: '10px 12px', color: entryOverdue ? '#ef4444' : '#555', fontWeight: entryOverdue ? 700 : 400 }}>{fmtDate(entryDue)}</td>
                      <td style={{ padding: '10px 12px' }}>
                        {entry.paymentReceivedAt ? (
                          <span style={{ color: '#059669' }}>{fmtDate(entry.paymentReceivedAt)}</span>
                        ) : entry.billSentAt ? (
                          <button
                            onClick={() => { setPaymentMonth(entry.month); setPaymentDate(new Date().toISOString().slice(0, 10)); setPaymentModal(true); }}
                            style={{ fontSize: '0.75rem', color: 'var(--primary)', background: 'none', border: '1px solid var(--primary)', borderRadius: 5, padding: '2px 8px', cursor: 'pointer', fontFamily: 'var(--font-body)' }}
                          >
                            Log
                          </button>
                        ) : <span style={{ color: '#aaa' }}>—</span>}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ background: statusBg, color: statusColor, borderRadius: 99, fontSize: '0.7rem', fontWeight: 700, padding: '2px 10px' }}>
                          {statusBadge}
                        </span>
                        {entry.unlockedAt && (
                          <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 3 }}>
                            Unlocked: {fmtDate(entry.unlockedAt)}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Payment Modal ── */}
      {paymentModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: '28px 28px', width: 360, boxShadow: '0 12px 40px rgba(0,0,0,0.18)' }}>
            <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 18, color: '#111' }}>
              💰 Log Payment — {paymentMonth}
            </div>
            <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Date payment received
            </label>
            <input
              type="date"
              className="form-control"
              value={paymentDate}
              max={new Date().toISOString().slice(0, 10)}
              onChange={e => setPaymentDate(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box', marginBottom: 20 }}
            />
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={submitPayment} disabled={paymentSaving} className="btn btn-primary" style={{ flex: 1 }}>
                {paymentSaving ? 'Saving…' : 'Log Payment'}
              </button>
              <button onClick={() => setPaymentModal(false)} className="btn btn-outline" style={{ flex: 1 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Unlock Modal ── */}
      {unlockModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: '28px 28px', width: 400, boxShadow: '0 12px 40px rgba(0,0,0,0.18)' }}>
            <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 6, color: '#111' }}>
              🔓 Unlock Credit — {companyName}
            </div>
            <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 18 }}>
              Enter reason for unlocking despite overdue payment. This is saved to the billing log.
            </div>
            <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Reason *
            </label>
            <textarea
              className="form-control"
              rows={3}
              value={unlockReason}
              onChange={e => setUnlockReason(e.target.value)}
              placeholder="e.g. Customer requested extension, payment expected by 30th..."
              style={{ width: '100%', boxSizing: 'border-box', marginBottom: 20, fontSize: '0.85rem' }}
            />
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={submitUnlock} disabled={unlockSaving || !unlockReason.trim()} className="btn btn-primary" style={{ flex: 1 }}>
                {unlockSaving ? 'Unlocking…' : 'Unlock Credit'}
              </button>
              <button onClick={() => { setUnlockModal(false); setUnlockReason(''); }} className="btn btn-outline" style={{ flex: 1 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthlyBillingCard;
